<script setup lang="ts">
import { shallowRef, watch, nextTick, onBeforeUnmount, computed, useSlots } from "vue";
import { lockBodyScroll } from "@/helpers/bodyScrollLock";
import Button, { type ButtonVariant } from "./Button.vue";

export type ModalPlacement = "bottom" | "center" | "responsive";

interface Props {
  modelValue?: boolean;
  title?: string;
  imageSrc?: string;
  primaryText?: string;
  secondaryText?: string;
  primaryVariant?: ButtonVariant;
  secondaryVariant?: ButtonVariant;
  dismissible?: boolean;
  placement?: ModalPlacement;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  title: undefined,
  imageSrc: undefined,
  primaryText: undefined,
  secondaryText: undefined,
  primaryVariant: "primary",
  secondaryVariant: "ghost",
  dismissible: true,
  placement: "responsive",
});

const emit = defineEmits<{
  (event: "update:modelValue", value: boolean): void;
  (event: "primary"): void;
  (event: "secondary"): void;
  (event: "dismiss"): void;
}>();

const slots = useSlots();
const hasMedia = computed(() => Boolean(slots.media || props.imageSrc));

let uid = 0;
const titleId = `pj-modal-title-${(uid += 1)}`;

const panelRef = shallowRef<HTMLElement | null>(null);
let lastFocused: HTMLElement | null = null;
let releaseScrollLock: (() => void) | undefined;

function onPrimary() {
  emit("primary");
}

function onSecondary() {
  emit("secondary");
}

function onDismiss() {
  if (!props.dismissible) return;
  emit("dismiss");
  emit("update:modelValue", false);
}

function focusableElements(): HTMLElement[] {
  if (!panelRef.value) return [];
  return Array.from(
    panelRef.value.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
    ),
  );
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === "Escape") {
    onDismiss();
    return;
  }
  if (event.key !== "Tab") return;
  // Trap focus within the panel while the modal is open.
  const focusable = focusableElements();
  if (focusable.length === 0) {
    event.preventDefault();
    panelRef.value?.focus();
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;
  if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

function teardown() {
  if (typeof document === "undefined") return;
  document.removeEventListener("keydown", onKeydown);
  releaseScrollLock?.();
  releaseScrollLock = undefined;
}

watch(
  () => props.modelValue,
  (open) => {
    if (typeof document === "undefined") return;
    if (open) {
      lastFocused = document.activeElement as HTMLElement | null;
      document.addEventListener("keydown", onKeydown);
      releaseScrollLock = lockBodyScroll();
      nextTick(() => {
        (focusableElements()[0] ?? panelRef.value)?.focus();
      });
    } else {
      teardown();
      lastFocused?.focus?.();
      lastFocused = null;
    }
  },
  { immediate: true },
);

onBeforeUnmount(teardown);
</script>

<template>
  <Teleport to="body">
    <Transition name="pj-modal">
      <div
        v-if="modelValue"
        class="pj-modal"
        :class="`pj-modal--${placement}`"
        @click.self="onDismiss"
      >
        <div
          ref="panelRef"
          class="pj-modal__panel"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="title ? titleId : undefined"
          tabindex="-1"
        >
          <button
            v-if="dismissible"
            type="button"
            class="pj-modal__close"
            aria-label="Close"
            @click="onDismiss"
          >
            <svg
              viewBox="0 0 16 16"
              width="16"
              height="16"
              fill="none"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M4 4L12 12M12 4L4 12"
                stroke="currentColor"
                stroke-width="1.75"
                stroke-linecap="round"
              />
            </svg>
          </button>

          <div v-if="hasMedia" class="pj-modal__media">
            <slot name="media">
              <img :src="imageSrc" alt="" class="pj-modal__image" />
            </slot>
          </div>

          <div class="pj-modal__body">
            <h2 v-if="title" :id="titleId" class="pj-modal__title">
              {{ title }}
            </h2>
            <slot />
          </div>

          <div v-if="primaryText || secondaryText" class="pj-modal__actions">
            <Button
              v-if="primaryText"
              :variant="primaryVariant"
              full-width
              @click="onPrimary"
            >
              {{ primaryText }}
            </Button>
            <Button
              v-if="secondaryText"
              :variant="secondaryVariant"
              full-width
              @click="onSecondary"
            >
              {{ secondaryText }}
            </Button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
.pj-modal {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
}

.pj-modal__panel {
  position: relative;
  width: 100%;
  max-width: 480px;
  max-height: 100dvh;
  overflow-y: auto;
  background: var(--pj-color-surface-default, #ffffff);
  padding: var(--pj-space-6) var(--pj-space-5) var(--pj-space-8);
}

/* Placement: bottom sheet ---------------------------------------------------*/
.pj-modal--bottom {
  align-items: flex-end;
}
.pj-modal--bottom .pj-modal__panel {
  border-radius: var(--pj-radius-xl) var(--pj-radius-xl) 0 0;
}

/* Placement: centered modal -------------------------------------------------*/
.pj-modal--center {
  align-items: center;
}
.pj-modal--center .pj-modal__panel {
  margin: var(--pj-space-4);
  max-height: calc(100dvh - 2 * var(--pj-space-4));
  border-radius: var(--pj-radius-xl);
}

/* Placement: responsive — sheet on small screens, modal from 640px up -------*/
.pj-modal--responsive {
  align-items: flex-end;
}
.pj-modal--responsive .pj-modal__panel {
  border-radius: var(--pj-radius-xl) var(--pj-radius-xl) 0 0;
}
@media (min-width: 640px) {
  .pj-modal--responsive {
    align-items: center;
  }
  .pj-modal--responsive .pj-modal__panel {
    margin: var(--pj-space-4);
    max-height: calc(100dvh - 2 * var(--pj-space-4));
    border-radius: var(--pj-radius-xl);
  }
}

.pj-modal__close {
  position: absolute;
  top: var(--pj-space-3);
  right: var(--pj-space-3);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: var(--pj-radius-sm);
  background: transparent;
  color: var(--pj-color-brand-gray-850, #222926);
  cursor: pointer;
  transition: background-color var(--pj-motion-duration-fast)
    var(--pj-motion-easing-standard);

  &:hover {
    background: var(--pj-color-brand-gray-150, #f1f0ee);
  }
}

.pj-modal__media {
  display: flex;
  justify-content: center;
  margin-top: var(--pj-space-4);
}

.pj-modal__image {
  width: 60px;
  height: 60px;
  object-fit: contain;
}

.pj-modal__body {
  padding: var(--pj-space-5) var(--pj-space-2);
  text-align: center;
  font-family: var(--pj-font-family-sans);
  font-size: var(--pj-font-size-base);
  font-weight: var(--pj-font-weight-regular);
  line-height: var(--pj-font-line-height-base);
  color: var(--pj-color-text-default, #3f4340);
}

.pj-modal__title {
  margin: 0 0 var(--pj-space-3);
  font-family: var(--pj-font-family-sans);
  font-size: var(--pj-font-size-lg);
  font-weight: var(--pj-font-weight-semibold);
  line-height: 24px;
  color: var(--pj-color-brand-gray-850, #222926);
}

.pj-modal__actions {
  display: flex;
  flex-direction: column;
  gap: var(--pj-space-3);
}

.pj-modal-enter-active,
.pj-modal-leave-active {
  transition: opacity var(--pj-motion-duration-base)
    var(--pj-motion-easing-standard);
}
.pj-modal-enter-active .pj-modal__panel {
  transition: transform var(--pj-motion-duration-base)
    var(--pj-motion-easing-decelerate);
}
.pj-modal-leave-active .pj-modal__panel {
  transition: transform var(--pj-motion-duration-base)
    var(--pj-motion-easing-accelerate);
}
.pj-modal-enter-from,
.pj-modal-leave-to {
  opacity: 0;
}

/* Slide for bottom + responsive(small) */
.pj-modal--bottom.pj-modal-enter-from .pj-modal__panel,
.pj-modal--bottom.pj-modal-leave-to .pj-modal__panel,
.pj-modal--responsive.pj-modal-enter-from .pj-modal__panel,
.pj-modal--responsive.pj-modal-leave-to .pj-modal__panel {
  transform: translateY(100%);
}

/* Scale for center + responsive(>=640px) */
.pj-modal--center.pj-modal-enter-from .pj-modal__panel,
.pj-modal--center.pj-modal-leave-to .pj-modal__panel {
  transform: scale(0.96);
}
@media (min-width: 640px) {
  .pj-modal--responsive.pj-modal-enter-from .pj-modal__panel,
  .pj-modal--responsive.pj-modal-leave-to .pj-modal__panel {
    transform: scale(0.96);
  }
}

@media (prefers-reduced-motion: reduce) {
  .pj-modal-enter-active,
  .pj-modal-leave-active,
  .pj-modal-enter-active .pj-modal__panel,
  .pj-modal-leave-active .pj-modal__panel {
    transition-duration: 0.01ms;
  }
}
</style>
